import { useState, useEffect } from "react";
import axios from "axios";
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Minus,
  RefreshCw,
  AlertTriangle,
  Loader2,
  Activity,
  Globe,
  ArrowRight
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TREND_CONFIG = {
  BULLISH: { color: "text-green-500", bg: "bg-green-500/10", icon: TrendingUp },
  BEARISH: { color: "text-red-500", bg: "bg-red-500/10", icon: TrendingDown },
  NEUTRAL: { color: "text-gray-500", bg: "bg-gray-500/10", icon: Minus },
  UNKNOWN: { color: "text-gray-500", bg: "bg-gray-500/10", icon: Minus },
};

const FX_LINKS = [
  { path: "/aura-eurusd", name: "EUR/USD", correlation: -0.95 },
  { path: "/aura-gbpusd", name: "GBP/USD", correlation: -0.90 },
  { path: "/aura-usdjpy", name: "USD/JPY", correlation: 0.85 },
];

const AuraDXY = () => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalysis();
  }, []);

  const fetchAnalysis = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/aura/dxy/analysis`);
      setAnalysis(response.data);
    } catch (error) {
      console.error("Error fetching DXY analysis:", error);
      toast.error("Failed to fetch DXY analysis");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <span className="ml-2">Loading DXY analysis...</span>
      </div>
    );
  }

  const trendConfig = TREND_CONFIG[analysis?.dxy?.trend] || TREND_CONFIG.NEUTRAL;
  const TrendIcon = trendConfig.icon;

  return (
    <div className="space-y-6" data-testid="aura-dxy-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <DollarSign className="w-6 h-6 text-primary" />
            DXY - Dollar Index
          </h1>
          <p className="text-muted-foreground text-sm">
            Macro indicator for FX pair analysis (not directly tradeable)
          </p>
        </div>
        <Button onClick={fetchAnalysis} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Main DXY Card */}
      <Card className={`${trendConfig.bg} border-2`}>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-full ${trendConfig.bg}`}>
                <TrendIcon className={`w-8 h-8 ${trendConfig.color}`} />
              </div>
              <div>
                <div className="text-4xl font-bold font-mono">{analysis?.dxy?.price}</div>
                <div className={`text-sm ${trendConfig.color} font-medium`}>
                  {analysis?.dxy?.trend} TREND
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-4">
                <div>
                  <span className="text-xs text-muted-foreground">1D Change</span>
                  <div className={`text-xl font-bold ${analysis?.dxy?.change_1d >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                    {analysis?.dxy?.change_1d >= 0 ? '+' : ''}{analysis?.dxy?.change_1d}%
                  </div>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">1W Change</span>
                  <div className={`text-xl font-bold ${analysis?.dxy?.change_1w >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                    {analysis?.dxy?.change_1w >= 0 ? '+' : ''}{analysis?.dxy?.change_1w}%
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Moving Averages */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <span className="text-xs text-muted-foreground">50 DMA</span>
            <div className="text-lg font-mono font-bold">{analysis?.dxy?.dma_50}</div>
            <Badge variant={analysis?.dxy?.above_50dma ? "default" : "destructive"} className="mt-1">
              {analysis?.dxy?.above_50dma ? "Above" : "Below"}
            </Badge>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <span className="text-xs text-muted-foreground">200 DMA</span>
            <div className="text-lg font-mono font-bold">{analysis?.dxy?.dma_200}</div>
            <Badge variant={analysis?.dxy?.above_200dma ? "default" : "destructive"} className="mt-1">
              {analysis?.dxy?.above_200dma ? "Above" : "Below"}
            </Badge>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <span className="text-xs text-muted-foreground">VIX</span>
            <div className={`text-lg font-bold ${analysis?.vix < 20 ? 'text-green-500' : analysis?.vix > 25 ? 'text-red-500' : ''}`}>
              {analysis?.vix}
            </div>
            <Badge variant="outline" className="mt-1">
              {analysis?.risk_environment}
            </Badge>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <span className="text-xs text-muted-foreground">Risk Environment</span>
            <div className={`text-lg font-bold ${
              analysis?.risk_environment === 'RISK-ON' ? 'text-green-500' : 
              analysis?.risk_environment === 'RISK-OFF' ? 'text-red-500' : ''
            }`}>
              {analysis?.risk_environment}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Interpretation */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Activity className="w-4 h-4" /> Market Interpretation
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2">
            {analysis?.interpretation?.map((point, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm">
                <span className="text-primary">•</span>
                {point}
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {/* FX Implications */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Globe className="w-4 h-4" /> FX Pair Implications
          </CardTitle>
          <CardDescription>Based on DXY correlation</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {FX_LINKS.map((fx) => {
              const implication = analysis?.fx_implications?.[fx.name.replace("/", "")];
              return (
                <Link key={fx.path} to={fx.path}>
                  <Card className="hover:border-primary transition-colors cursor-pointer">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">{fx.name}</span>
                        <Badge variant={
                          implication === "Bullish" ? "default" : 
                          implication === "Bearish" ? "destructive" : "secondary"
                        }>
                          {implication}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between text-sm text-muted-foreground">
                        <span>DXY Correlation: {fx.correlation}</span>
                        <ArrowRight className="w-4 h-4" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Warning */}
      <Card className="bg-amber-500/10 border-amber-500/30">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
            <div>
              <span className="font-medium text-amber-500">Macro Indicator Only</span>
              <p className="text-sm text-muted-foreground mt-1">
                DXY is not directly tradeable on most retail platforms. Use this analysis to inform 
                your FX pair trading decisions. Click on any pair above to see its full thesis.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AuraDXY;
